import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";

import { provincesRepo } from "data/repositories/provincesRepo";
import { themes } from "data/dbsSeed";

function emptyProvince() {
  return {
    id: "",
    name: "",
    slogan: "",
    story: "",
    heroImage: null,
    themes: [],
    highlights: [],
    experiences: [],
    seasons: ["Spring", "Summer", "Autumn", "Winter"],
  };
}

export default function ProvinceEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const provinceId = params.id;
  const isNew = provinceId === "new" || !provinceId;

  const [form, setForm] = useState(emptyProvince());
  const [highlightsJson, setHighlightsJson] = useState("[]");
  const [experiencesJson, setExperiencesJson] = useState("[]");
  const [error, setError] = useState("");

  const readOnlyId = !isNew;

  const title = isNew ? "Yeni il" : `İl düzenle: ${provinceId}`;

  useEffect(() => {
    setError("");
    if (isNew) {
      const next = emptyProvince();
      setForm(next);
      setHighlightsJson(JSON.stringify(next.highlights, null, 2));
      setExperiencesJson(JSON.stringify(next.experiences, null, 2));
      return;
    }

    const current = provincesRepo.getById(provinceId);
    if (!current) {
      setError("İl bulunamadı");
      return;
    }
    setForm(current);
    setHighlightsJson(JSON.stringify(current.highlights || [], null, 2));
    setExperiencesJson(JSON.stringify(current.experiences || [], null, 2));
  }, [isNew, provinceId]);

  const selectedThemes = useMemo(() => new Set(form.themes || []), [form.themes]);

  const toggleTheme = (t) => {
    const next = new Set(selectedThemes);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    setForm((prev) => ({ ...prev, themes: Array.from(next) }));
  };

  const onSave = (e) => {
    e.preventDefault();
    setError("");

    if (!form.id || !form.name) {
      setError("ID ve Ad zorunlu");
      return;
    }

    let highlights;
    let experiences;
    try {
      highlights = JSON.parse(highlightsJson);
      experiences = JSON.parse(experiencesJson);
      if (!Array.isArray(highlights) || !Array.isArray(experiences)) {
        throw new Error("Array bekleniyor");
      }
    } catch {
      setError("Highlights/Experiences JSON geçersiz. Array formatında olmalı.");
      return;
    }

    const payload = {
      ...form,
      highlights,
      experiences,
      seasons: Array.isArray(form.seasons)
        ? form.seasons
        : String(form.seasons || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
    };

    try {
      if (isNew) {
        provincesRepo.create(payload);
      } else {
        provincesRepo.update(form.id, payload);
      }
      navigate("/admin/content/provinces");
    } catch (err) {
      setError(err?.message || "Kaydetme hatası");
    }
  };

  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-5 pt-md-7">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="8" xs="12">
                <h6 className="h2 text-white d-inline-block mb-0">{title}</h6>
              </Col>
              <Col lg="4" xs="12" className="text-right">
                <Button tag={Link} to="/admin/content/provinces" color="secondary" size="sm">
                  Listeye dön
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Temel bilgiler</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}

                <Form onSubmit={onSave}>
                  <FormGroup>
                    <Label>ID (slug)</Label>
                    <Input
                      value={form.id}
                      onChange={(e) => setForm((p) => ({ ...p, id: e.target.value }))}
                      placeholder="ör: trabzon"
                      disabled={readOnlyId}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Ad</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Trabzon"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Slogan</Label>
                    <Input
                      value={form.slogan}
                      onChange={(e) => setForm((p) => ({ ...p, slogan: e.target.value }))}
                      placeholder="Kısa slogan"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Hikaye</Label>
                    <Input
                      type="textarea"
                      rows="6"
                      value={form.story}
                      onChange={(e) => setForm((p) => ({ ...p, story: e.target.value }))}
                      placeholder="Storytelling metni"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Sezonlar (virgülle)</Label>
                    <Input
                      value={Array.isArray(form.seasons) ? form.seasons.join(", ") : String(form.seasons || "")}
                      onChange={(e) => setForm((p) => ({ ...p, seasons: e.target.value }))}
                      placeholder="Spring, Summer, Autumn, Winter"
                    />
                  </FormGroup>

                  <div className="mb-3">
                    <Label className="d-block">Temalar</Label>
                    <Row>
                      {themes.map((t) => (
                        <Col md="4" key={t} className="mb-2">
                          <div className="custom-control custom-checkbox">
                            <input
                              className="custom-control-input"
                              id={`theme-${t}`}
                              type="checkbox"
                              checked={selectedThemes.has(t)}
                              onChange={() => toggleTheme(t)}
                            />
                            <label className="custom-control-label" htmlFor={`theme-${t}`}>
                              {t}
                            </label>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>

                  <Button color="primary" type="submit">
                    Kaydet
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">İçerik (JSON)</h3>
              </CardHeader>
              <CardBody>
                <p className="text-muted" style={{ fontSize: 12 }}>
                  MVP için hızlı CRUD: highlights/experiences alanlarını JSON array olarak düzenleyin.
                  TODO(firebase): Bu alanlar ayrı alt-koleksiyonlar olabilir.
                </p>

                <FormGroup>
                  <Label>Highlights (array)</Label>
                  <Input type="textarea" rows="8" value={highlightsJson} onChange={(e) => setHighlightsJson(e.target.value)} />
                </FormGroup>

                <FormGroup>
                  <Label>Experiences (array)</Label>
                  <Input type="textarea" rows="8" value={experiencesJson} onChange={(e) => setExperiencesJson(e.target.value)} />
                </FormGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
